import { useMemo, useState } from 'react';
import { ArrDetailModal } from './arrShared';
import { DevOverlay } from '../../components/DevOverlay/DevOverlay';

// Deliberately avoids cyan/green — the "Total Sourced ARR" and "Top Source"
// summary cards already use those, and reusing them here made every card
// blur together.
const SRC_COLORS = ['#7c3aed', '#d97706', '#dc2626', '#4338ca', '#be185d', '#78350f', '#9333ea', '#b45309'];

const EMPTY_ARRAY = [];

const rowMonthArr = (row, month) => Number(row?.monthly_arr?.[month] || 0);

const shortMoney = (value) => {
  const n = Number(value || 0);
  const a = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${Math.round(a / 1e3).toFixed(1)}K`;
  return `${sign}$${Math.round(a).toLocaleString()}`;
};

// Exact port of the reference's fF() — whole-dollar money, no decimals ever.
const fF = (v) => {
  const n = Number(v || 0);
  return `${n < 0 ? '-' : ''}$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;
};

const monthRange = (from, to) => {
  if (!from || !to) return [];
  const months = [];
  let [year, month] = from.split('-').map(Number);
  const [endYear, endMonth] = to.split('-').map(Number);
  while (year < endYear || (year === endYear && month <= endMonth)) {
    months.push(`${year}-${String(month).padStart(2, '0')}`);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return months;
};

const changeValue = (changes, month, key) => {
  const raw = changes?.[month] || {};
  const canonical = {
    new: ['new', 'New'],
    upsell: ['upsell', 'Upsell'],
    churn: ['churn', 'Churn'],
    downsell: ['downsell', 'Downsell', 'DOWNSELL'],
  }[key] || [key];
  return canonical.reduce((sum, name) => sum + Number(raw[name] || 0), 0);
};

function ArrBySourceTab({ data }) {
  const [modal, setModal] = useState(null);
  const [sortKey, setSortKey] = useState('arr');
  const [sortAsc, setSortAsc] = useState(false);

  const records = data.records || EMPTY_ARRAY;
  const trend = data.trend || EMPTY_ARRAY;
  const periodFrom = data.period?.from || '';

  const latestMonth = useMemo(() => {
    if (data.period?.to) return data.period.to;
    const months = new Set(trend.map(row => row.month).filter(Boolean));
    records.forEach(row => {
      Object.entries(row.monthly_arr || {}).forEach(([month, value]) => {
        if (Number(value) > 0) months.add(month);
      });
    });
    return [...months].sort().at(-1) || '';
  }, [records, trend, data.period?.to]);

  // Deal Source (BookingRecord.deal_source, imported as "Deal Source" —
  // e.g. Marketing, Channel Partner, Direct, Referral) tells us WHERE a
  // booking originated. Grouped the same way every other "ARR by X" view
  // in this app groups: active ARR as of the latest month, plus period
  // movement (New/Expansion/Churn/Downsell) summed across the filter
  // bar's selected period (YTD/QTD/LTM/Custom).
  const bySource = useMemo(() => {
    if (!latestMonth) return { rows: [], totalArr: 0, trackedArr: 0, unspecifiedArr: 0 };
    const year = latestMonth.slice(0, 4);
    const months = monthRange(periodFrom || `${year}-01`, latestMonth);

    const groups = {};
    records.forEach(row => {
      const arr = rowMonthArr(row, latestMonth);
      const source = (row.deal_source || '').trim() || 'Unspecified';

      let mNew = 0;
      let mUpsell = 0;
      let mChurn = 0;
      let mDownsell = 0;
      months.forEach(month => {
        const changes = row.monthly_changes || {};
        mNew += Math.max(0, changeValue(changes, month, 'new'));
        mUpsell += Math.max(0, changeValue(changes, month, 'upsell'));
        mChurn += changeValue(changes, month, 'churn');
        mDownsell += changeValue(changes, month, 'downsell');
      });

      // A deal that fully churned/downsold to $0 ARR by latestMonth still has
      // real New/Expansion/Churn/Downsell movement that must be counted here
      // — matching how the ARR Dashboard's own KPI cards sum movement across
      // EVERY deal, not just ones still carrying ARR today. Only a row with
      // neither current ARR nor any movement this period contributes nothing.
      if (arr === 0 && !mNew && !mUpsell && !mChurn && !mDownsell) return;

      if (!groups[source]) {
        groups[source] = {
          source, arr: 0, deals: 0, customers: new Set(),
          new: 0, upsell: 0, churn: 0, downsell: 0,
        };
      }
      const g = groups[source];
      // ARR / deal / customer counts stay netted at $0 (skips true no-op
      // rows, but keeps negative correction rows so the total agrees with
      // Total ARR everywhere else in the dashboard).
      if (arr !== 0) {
        g.arr += arr;
        g.deals += 1;
        g.customers.add(row.end_user || row.bill_to || 'Unspecified');
      }
      g.new += mNew;
      g.upsell += mUpsell;
      g.churn += mChurn;
      g.downsell += mDownsell;
    });

    const totalArr = Object.values(groups).reduce((sum, g) => sum + g.arr, 0);
    const rows = Object.values(groups).map(g => ({
      ...g,
      customers: g.customers.size,
      percent: totalArr > 0 ? (g.arr / totalArr) * 100 : 0,
    }));
    const trackedArr = rows.filter(r => r.source !== 'Unspecified').reduce((sum, r) => sum + r.arr, 0);
    const unspecifiedArr = totalArr - trackedArr;

    return { rows, totalArr, trackedArr, unspecifiedArr };
  }, [records, latestMonth, periodFrom]);

  const trackedRows = useMemo(
    () => bySource.rows.filter(r => r.source !== 'Unspecified').sort((a, b) => b.arr - a.arr),
    [bySource.rows]
  );

  const sortedRows = useMemo(() => {
    return [...bySource.rows].sort((a, b) => {
      if (sortKey === 'source') {
        return sortAsc ? a.source.localeCompare(b.source) : b.source.localeCompare(a.source);
      }
      const av = Number(a[sortKey] ?? 0);
      const bv = Number(b[sortKey] ?? 0);
      return sortAsc ? av - bv : bv - av;
    });
  }, [bySource.rows, sortKey, sortAsc]);

  function toggleSort(key) {
    if (sortKey === key) setSortAsc(v => !v);
    else { setSortKey(key); setSortAsc(false); }
  }

  const sourceTotals = useMemo(() => bySource.rows.reduce((sum, row) => ({
    arr: sum.arr + row.arr,
    new: sum.new + row.new,
    upsell: sum.upsell + row.upsell,
    churn: sum.churn + row.churn,
    downsell: sum.downsell + row.downsell,
  }), { arr: 0, new: 0, upsell: 0, churn: 0, downsell: 0 }), [bySource.rows]);

  const sourceDetail = (sourceName) => {
    const row = bySource.rows.find(r => r.source === sourceName);
    if (!row) return null;

    const custMap = {};
    const repMap = {};
    const regionMap = {};
    records.forEach(r => {
      const source = (r.deal_source || '').trim() || 'Unspecified';
      if (source !== sourceName) return;
      const arr = rowMonthArr(r, latestMonth);
      if (arr === 0) return;
      const cust = r.end_user || r.bill_to || 'Unspecified';
      custMap[cust] = (custMap[cust] || 0) + arr;
      const rep = r.sales_person || 'Unassigned';
      repMap[rep] = (repMap[rep] || 0) + arr;
      const region = r.business_unit || 'Unspecified';
      regionMap[region] = (regionMap[region] || 0) + arr;
    });

    const toItems = (map, color) => Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .map(([label, value]) => ({
        label,
        value: fF(value),
        percent: row.arr > 0 ? (value / row.arr) * 100 : 0,
        color,
      }));

    return {
      title: `${sourceName} — ARR by Source`,
      formula: `Active ARR as of ${latestMonth}`,
      big: fF(row.arr),
      color: '#7c3aed',
      stats: [
        { label: 'ARR', value: fF(row.arr), color: '#7c3aed' },
        { label: '% of Total ARR', value: `${row.percent.toFixed(1)}%`, color: '#0f2d52' },
      ],
      insight: `${sourceName} contributed ${fF(row.arr)} in active ARR as of ${latestMonth}.`,
      sections: [
        { title: '👤 Customers', color: '#7c3aed', items: toItems(custMap, '#7c3aed') },
        { title: '🧑‍💼 By Sales Rep', color: '#0891b2', items: toItems(repMap, '#0891b2') },
        { title: '🌍 By Business Unit', color: '#059669', items: toItems(regionMap, '#059669') },
      ],
    };
  };

  const openSourceModal = (sourceName) => setModal(sourceDetail(sourceName));

  const MOVEMENT_META = {
    new: { label: 'New', icon: '🆕', color: '#059669' },
    upsell: { label: 'Expansion', icon: '📈', color: '#059669' },
    churn: { label: 'Churn', icon: '📉', color: '#dc2626' },
    downsell: { label: 'Downsell', icon: '🔻', color: '#dc2626' },
  };

  const movementDetail = (sourceName, metricKey) => {
    const row = bySource.rows.find(r => r.source === sourceName);
    if (!row) return null;
    const meta = MOVEMENT_META[metricKey];
    const year = latestMonth.slice(0, 4);
    const from = periodFrom || `${year}-01`;
    const months = monthRange(from, latestMonth);

    const custMap = {};
    const repMap = {};
    const regionMap = {};
    records.forEach(r => {
      const source = (r.deal_source || '').trim() || 'Unspecified';
      if (source !== sourceName) return;
      const changes = r.monthly_changes || {};
      let total = 0;
      months.forEach(month => {
        const v = changeValue(changes, month, metricKey);
        total += (metricKey === 'new' || metricKey === 'upsell') ? Math.max(0, v) : v;
      });
      if (!total) return;
      const cust = r.end_user || r.bill_to || 'Unspecified';
      custMap[cust] = (custMap[cust] || 0) + total;
      const rep = r.sales_person || 'Unassigned';
      repMap[rep] = (repMap[rep] || 0) + total;
      const region = r.business_unit || 'Unspecified';
      regionMap[region] = (regionMap[region] || 0) + total;
    });

    const groupTotal = Object.values(custMap).reduce((sum, v) => sum + v, 0);
    const toMovementItems = (map) => Object.entries(map)
      .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
      .map(([label, value]) => ({
        label,
        value: fF(value),
        percent: groupTotal !== 0 ? (Math.abs(value) / Math.abs(groupTotal)) * 100 : 0,
        color: meta.color,
      }));

    return {
      title: `${sourceName} — ${meta.label}`,
      formula: `${meta.label} activity · ${from} to ${latestMonth}`,
      big: fF(groupTotal),
      color: meta.color,
      stats: [
        { label: meta.label, value: fF(groupTotal), color: meta.color },
        { label: '% of Source ARR', value: row.arr > 0 ? `${((Math.abs(groupTotal) / row.arr) * 100).toFixed(1)}%` : '-', color: '#0f2d52' },
      ],
      insight: `${sourceName} recorded ${fF(groupTotal)} in ${meta.label.toLowerCase()} activity from ${from} to ${latestMonth}.`,
      sections: [
        { title: `${meta.icon} Customers`, color: meta.color, items: toMovementItems(custMap) },
        { title: '🧑‍💼 By Sales Rep', color: '#0891b2', items: toMovementItems(repMap) },
        { title: '🌍 By Business Unit', color: '#059669', items: toMovementItems(regionMap) },
      ],
    };
  };

  const openMetricModal = (sourceName, metricKey) => setModal(movementDetail(sourceName, metricKey));

  const cardSources = trackedRows.slice(0, 4);

  return (
    <>
      <ArrDetailModal detail={modal} onClose={() => setModal(null)} />

      <div className="arr-src-kpis">
        <DevOverlay name="ARR by Source: Total Tracked">
          <div className="arr-t2kcard arr-t2kcard-cyan">
            <div className="arr-t2kcard-label">Total Sourced ARR</div>
            <div className="arr-t2kcard-val">{shortMoney(bySource.trackedArr)}</div>
            <div className="arr-t2kcard-sub">
              {trackedRows.length} source{trackedRows.length === 1 ? '' : 's'} tracked
              {bySource.unspecifiedArr > 0 ? ` · ${shortMoney(bySource.unspecifiedArr)} unspecified` : ''}
            </div>
          </div>
        </DevOverlay>
        {cardSources.map((row, i) => (
          <DevOverlay name={`ARR by Source: ${row.source}`} key={row.source}>
            <div
              className="arr-t2kcard"
              style={{ background: `linear-gradient(135deg, ${SRC_COLORS[i % SRC_COLORS.length]}cc 0%, ${SRC_COLORS[i % SRC_COLORS.length]} 100%)` }}
              onClick={() => openSourceModal(row.source)}
            >
              <div className="arr-t2kcard-label">{row.source}</div>
              <div className="arr-t2kcard-val">{shortMoney(row.arr)}</div>
              <div className="arr-t2kcard-sub">{row.percent.toFixed(1)}% of total ARR</div>
            </div>
          </DevOverlay>
        ))}
      </div>

      {!trackedRows.length && (
        <div className="arr-src-empty-note">
          No Deal Source data found in the current import — every active deal shows as
          "Unspecified" ({shortMoney(bySource.unspecifiedArr)} total). Once bookings are
          imported with a "Deal Source" column populated (e.g. Marketing, Channel Partner,
          Direct, Referral), this page fills in automatically.
        </div>
      )}

      <DevOverlay name="ARR by Source Table">
        <div className="arr-chart-card">
          <div className="arr-chart-title">Source Deep Dive</div>
          <div className="arr-chart-sub">Every booking source · click a cell for its breakdown</div>
          <div className="arr-src-table-wrap">
            <table className="arr-src-table">
              <thead>
                <tr>
                  <th onClick={() => toggleSort('source')}>Source ↕</th>
                  <th className="right" onClick={() => toggleSort('arr')}>ARR ↕</th>
                  <th className="right" onClick={() => toggleSort('percent')}>% of Total ↕</th>
                  <th className="right" onClick={() => toggleSort('new')}>New ↕</th>
                  <th className="right" onClick={() => toggleSort('upsell')}>Expansion ↕</th>
                  <th className="right" onClick={() => toggleSort('churn')}>Churn ↕</th>
                  <th className="right" onClick={() => toggleSort('downsell')}>Downsell ↕</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(row => (
                  <tr key={row.source}>
                    <td className="arr-src-clickable" onClick={() => openSourceModal(row.source)}><strong>{row.source}</strong></td>
                    <td className="right arr-td-money arr-src-clickable" onClick={() => openSourceModal(row.source)}>{fF(row.arr)}</td>
                    <td className="right arr-src-clickable" onClick={() => openSourceModal(row.source)}>{row.percent.toFixed(1)}%</td>
                    <td
                      className={`right arr-up${row.new ? ' arr-src-clickable' : ''}`}
                      onClick={row.new ? () => openMetricModal(row.source, 'new') : undefined}
                    >
                      {row.new ? fF(row.new) : '-'}
                    </td>
                    <td
                      className={`right arr-up${row.upsell ? ' arr-src-clickable' : ''}`}
                      onClick={row.upsell ? () => openMetricModal(row.source, 'upsell') : undefined}
                    >
                      {row.upsell ? fF(row.upsell) : '-'}
                    </td>
                    <td
                      className={`right arr-dn${row.churn ? ' arr-src-clickable' : ''}`}
                      onClick={row.churn ? () => openMetricModal(row.source, 'churn') : undefined}
                    >
                      {row.churn ? fF(Math.abs(row.churn)) : '-'}
                    </td>
                    <td
                      className={`right arr-dn${row.downsell ? ' arr-src-clickable' : ''}`}
                      onClick={row.downsell ? () => openMetricModal(row.source, 'downsell') : undefined}
                    >
                      {row.downsell ? fF(Math.abs(row.downsell)) : '-'}
                    </td>
                  </tr>
                ))}
                {!sortedRows.length && (
                  <tr><td colSpan={7} className="bm-empty-row">No source data available.</td></tr>
                )}
              </tbody>
              {!!sortedRows.length && (
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td className="right">{fF(sourceTotals.arr)}</td>
                    <td className="right">100.0%</td>
                    <td className="right">{sourceTotals.new ? fF(sourceTotals.new) : '-'}</td>
                    <td className="right">{sourceTotals.upsell ? fF(sourceTotals.upsell) : '-'}</td>
                    <td className="right">{sourceTotals.churn ? fF(Math.abs(sourceTotals.churn)) : '-'}</td>
                    <td className="right">{sourceTotals.downsell ? fF(Math.abs(sourceTotals.downsell)) : '-'}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </DevOverlay>
    </>
  );
}

export default ArrBySourceTab;
export { ArrBySourceTab };
